import { useId } from 'react';
import {
  fieldKind,
  filterNumber,
  numericField,
  rangeField,
  type ColumnFilter,
  type FieldShape,
  type Sort,
} from '../../lib/field-values';

export function SortHeading({
  label,
  field,
  sort,
  onSort,
}: {
  label: string;
  field: string;
  sort: Sort;
  onSort: (s: Sort) => void;
}) {
  return (
    <button
      type="button"
      className="inline-flex w-full items-center justify-between gap-2 text-left"
      onClick={() =>
        onSort({
          key: field,
          direction: sort.key === field && sort.direction === 'asc' ? 'desc' : 'asc',
        })
      }
      title={`Ordina per ${label}`}
    >
      {label}
      <span aria-hidden="true">
        {sort.key === field ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

export const sortAria = (sort: Sort, key: string) =>
  sort.key === key
    ? sort.direction === 'asc'
      ? ('ascending' as const)
      : ('descending' as const)
    : ('none' as const);

export function ColumnControl({
  attr,
  label,
  value,
  options,
  onChange,
}: {
  attr: FieldShape;
  label: string;
  value: ColumnFilter;
  options?: Array<{ value: string; label: string }>;
  onChange: (f: ColumnFilter) => void;
}) {
  const id = useId();
  if (options || fieldKind(attr) === 'si_no')
    return (
      <select
        aria-label={`Filtra ${label}`}
        value={value.value ?? ''}
        onChange={(e) => onChange({ value: e.target.value })}
      >
        <option value="">Tutti</option>
        {(
          options ?? [
            { value: 'true', label: 'Sì' },
            { value: 'false', label: 'No' },
          ]
        ).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  if (rangeField(attr)) {
    const kind = fieldKind(attr);
    const type =
      kind === 'data'
        ? 'date'
        : kind === 'data_ora'
          ? 'datetime-local'
          : kind === 'ora'
            ? 'time'
            : 'text';
    const date = ['data', 'data_ora', 'ora'].includes(kind);
    return (
      <div className="flex min-w-52 gap-1.5">
        {(['min', 'max'] as const).map((bound) => (
          <label key={bound} className="min-w-0 flex-1 text-[10px]">
            <span className="mb-0.5 block">
              {date ? (bound === 'min' ? 'Dal' : 'Al') : bound === 'min' ? 'Min' : 'Max'}
            </span>
            <input
              lang="it-IT"
              type={type}
              inputMode={numericField(attr) ? 'decimal' : undefined}
              aria-invalid={
                !!value[bound] &&
                numericField(attr) &&
                (kind === 'anno'
                  ? !/^\d{4}$/.test(value[bound]!)
                  : filterNumber(value[bound]!) === null)
              }
              title={
                numericField(attr)
                  ? kind === 'anno'
                    ? 'Anno a quattro cifre'
                    : 'Numero italiano: 1.234,56'
                  : undefined
              }
              aria-label={`${bound === 'min' ? (date ? 'Dal' : 'Min') : date ? 'Al' : 'Max'}: ${label}`}
              placeholder={bound === 'min' ? 'Min' : 'Max'}
              value={value[bound] ?? ''}
              onChange={(e) => onChange({ ...value, [bound]: e.target.value })}
            />
            {!!value[bound] &&
              numericField(attr) &&
              (kind === 'anno'
                ? !/^\d{4}$/.test(value[bound]!)
                : filterNumber(value[bound]!) === null) && (
                <span className="mt-1 block">
                  {kind === 'anno' ? 'Usa quattro cifre' : 'Usa il formato 1.234,56'}
                </span>
              )}
          </label>
        ))}
      </div>
    );
  }
  return (
    <input
      id={id}
      aria-label={`Cerca ${label}`}
      placeholder="Cerca…"
      value={value.text ?? ''}
      onChange={(e) => onChange({ text: e.target.value })}
    />
  );
}
